import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Lock, Unlock } from 'lucide-react'
import { cx } from '@/lib/cx'
import { useAdminSession } from './AdminSessionProvider'
import { UnlockDialog } from './UnlockDialog'

/**
 * Lock control for a screen header, carried over from the original app.
 *
 * It used to sit in a full-width strip of its own above every screen, which
 * spent a whole row of a phone's viewport to show one 44px button and pushed
 * every heading down with it. On the heading line it costs nothing: the header
 * is already a flex row with the title on one side and room on the other.
 *
 * Owns its dialog rather than taking it as a prop, so a screen adds the control
 * with `action={<AdminLockButton />}` and nothing else.
 */
export function AdminLockButton() {
  const navigate = useNavigate()
  const { unlocked, lock } = useAdminSession()
  const [showUnlock, setShowUnlock] = useState(false)

  return (
    <>
      {/* Tapping it when unlocked locks again rather than silently doing
          nothing. */}
      <button
        type="button"
        onClick={() => {
          if (unlocked) void lock()
          else setShowUnlock(true)
        }}
        aria-label={unlocked ? 'Lock admin' : 'Unlock admin'}
        className={cx(
          'grid h-touch w-touch shrink-0 place-items-center rounded-2xl border transition-colors',
          unlocked
            ? 'border-brand-600 bg-brand-50 text-brand-600'
            : 'border-[var(--border-subtle)] bg-[var(--surface-raised)] text-[var(--text-muted)]',
        )}
      >
        {unlocked ? (
          <Unlock className="h-5 w-5" aria-hidden="true" />
        ) : (
          <Lock className="h-5 w-5" aria-hidden="true" />
        )}
      </button>

      {showUnlock && (
        <UnlockDialog
          onClose={() => setShowUnlock(false)}
          onUnlocked={() => {
            setShowUnlock(false)
            void navigate('/admin')
          }}
        />
      )}
    </>
  )
}
